import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import moment from 'moment';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import { ClientError } from '../../../services/central-api/base-service';
import { documentService } from '../../../services/central-api/document';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';
import FormActions from '../../../components/FormActions';
import MyAppText from '../../../components/MyAppText';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';

interface DocumentVersion {
    id: number;
    version: number;
    createdAt: string;
}

export default function DocumentVersionModal({ onClose, documentId, isModalVisible }: { onClose: () => void; documentId: number; isModalVisible: boolean; }) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [versions, setVersions] = useState<DocumentVersion[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<number>(0);

    function clearValues() {
        setVersions([]);
    }

    async function getVersions() {
        try {
            const data = await documentService.getDocumentVersions(documentId);
            setVersions(data);
        } catch (err) {
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            clearValues();
            onClose();
        }
    }

    useEffect(() => {
        if (isModalVisible && documentId) {
            getVersions();
        }
    }, [documentId, isModalVisible]);

    async function handleDownload() {
        try {
            const url = await documentService.getDocumentDownloadUrl(documentId, selectedVersion);
            openExternalLinkInNewTab(url);
            onClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            }
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 460 }]}>
                        <View style={{ gap: 24, minHeight: 120 }}>
                            <View style={{ flexDirection: 'column', gap: 8 }}>
                                <MyAppText style={styles.header}>{translate('downloadDocument')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('selectDocumentVersion')}</MyAppText>
                            </View>
                            <FilterDropdown
                                label={translate('documentVersion')}
                                required
                                semiBoldLabel={true}
                                labelFontSize={14}
                                items={versions.map(v => ({
                                    value: v.version,
                                    label: `v${v.version} - ${moment(v.createdAt).format('DD/MM/YYYY HH:mm:ss')}`,
                                }))}
                                multiple={false}
                                value={selectedVersion}
                                setValue={(val) => {
                                    const resolved = typeof val === 'function' ? val(selectedVersion) : val;
                                    setSelectedVersion(resolved as number);
                                }}
                                zIndex={2}
                            />
                        </View>

                        <FormActions
                            onSubmit={handleDownload}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            confirmText='downloadDocument'
                            disabled={!selectedVersion}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        padding: 32,
        rowGap: 25,
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
}));
